import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { getLocationOn, Colors } from '../../../helpers/navigationHelpers';
import styles from './BottomTabAIChat.module.css';

const BottomTabAIChat = () => {
  const [messages, setMessages] = useState([
    {
      id: '1',
      text: 'Hello! This is Tellmiz AI assistant. How can I help you today?',
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [locationOn, setLocationOn] = useState(false);
  const messagesEndRef = useRef(null);

  const presetPrompts = [
    { id: '1', text: 'What is Tellmiz?', label: 'About Tellmiz' },
    { id: '2', text: 'Show me available apartments', label: 'Apartments' },
    { id: '3', text: 'What services do you offer?', label: 'Services' },
    { id: '4', text: 'How can I book an event?', label: 'Events' },
  ];

  useEffect(() => {
    setLocationOn(getLocationOn());
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const primaryColor = locationOn ? Colors.CUSTOM_YELLOW : Colors.PRIMARY;

  const getAIResponse = (userInput) => {
    const lowerInput = userInput.toLowerCase();

    if (lowerInput.includes('tellmiz')) {
      return 'Tellmiz is an innovative AI-powered platform designed to revolutionize students learning experience. It provides personalized learning paths, quizzes, and other resources. On campus events are also available to enhance the learning experience.';
    }

    if (lowerInput.includes('apartment')) {
      return 'Currently, we have apartments available for students. The apartments are located in the heart of the campus and are equipped with all necessary amenities.';
    }

    if (lowerInput.includes('service')) {
      return 'We offer a wide range of services including: apartment rentals, event bookings, gaming tournaments, consultation services, restaurant reservations, and software development services.';
    }

    if (lowerInput.includes('event')) {
      return 'You can book events through our Events section! We offer various types of events including social gatherings, academic workshops, and entertainment events.';
    }

    return "That's an interesting question! Let me think about that. I'm here to help with information about Tellmiz services and features.";
  };

  const sendMessage = (customText) => {
    const messageText = customText || inputText.trim();
    if (messageText === '' || isTyping) return;

    const userMessage = {
      id: Date.now().toString(),
      text: messageText,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    setTimeout(() => {
      const aiResponseText = getAIResponse(messageText);
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        text: aiResponseText,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box
        className={styles.header}
        sx={{ backgroundColor: primaryColor }}
      >
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
          AI Assistant
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          Ask me anything!
        </Typography>
      </Box>

      {/* Messages */}
      <Box className={styles.messagesContainer}>
        {messages.map((message) => (
          <Box
            key={message.id}
            className={`${styles.messageWrapper} ${
              message.isUser ? styles.userMessage : styles.aiMessage
            }`}
          >
            {!message.isUser && (
              <Avatar
                sx={{
                  backgroundColor: primaryColor,
                  width: 32,
                  height: 32,
                  marginRight: 1,
                }}
              >
                <SmartToyIcon sx={{ fontSize: 20 }} />
              </Avatar>
            )}
            <Paper
              elevation={2}
              className={styles.messageBubble}
              sx={{
                backgroundColor: message.isUser ? primaryColor : 'white',
                color: message.isUser ? 'white' : '#333',
              }}
            >
              <Typography variant="body1">{message.text}</Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  marginTop: 0.5,
                  opacity: 0.7,
                }}
              >
                {message.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </Paper>
          </Box>
        ))}

        {isTyping && (
          <Box className={`${styles.messageWrapper} ${styles.aiMessage}`}>
            <Avatar
              sx={{
                backgroundColor: primaryColor,
                width: 32,
                height: 32,
                marginRight: 1,
              }}
            >
              <SmartToyIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Paper elevation={2} className={styles.messageBubble}>
              <Typography variant="body1">Typing...</Typography>
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Quick Questions */}
      <Box className={styles.quickQuestions}>
        <Typography variant="body2" gutterBottom>
          Quick Questions:
        </Typography>
        <Box className={styles.chipsContainer}>
          {presetPrompts.map((prompt) => (
            <Chip
              key={prompt.id}
              label={prompt.label}
              onClick={() => sendMessage(prompt.text)}
              disabled={isTyping}
              sx={{
                backgroundColor: primaryColor,
                color: 'white',
                '&:hover': {
                  backgroundColor: primaryColor,
                  opacity: 0.8,
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Input */}
      <Box className={styles.inputContainer}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          disabled={isTyping}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
            },
          }}
        />
        <IconButton
          onClick={() => sendMessage()}
          disabled={!inputText.trim() || isTyping}
          sx={{
            marginLeft: 1,
            backgroundColor: primaryColor,
            color: 'white',
            '&:hover': {
              backgroundColor: primaryColor,
              opacity: 0.8,
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default BottomTabAIChat;

















